import React from 'react';
import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip, usePlotArea } from 'recharts';
import { tokens } from '../../../../public/tokens';
import { CHART_COLORS, CHART_MARGIN, ChartFrame } from '../../../library/Chart';
import { LegendText, PIE_ANGLES } from './Charts.styles';

const { typography } = tokens;

/**
 * Two-line "Total / <sum>" text, centred in the plot area (which excludes the legend) like the donut.
 * @param {number} total Sum of all slice values
 */
function DonutTotal({ total }) {
    const plotArea = usePlotArea();

    if (!plotArea) {
        return null;
    }

    const centreX = plotArea.x + plotArea.width / 2;
    const centreY = plotArea.y + plotArea.height / 2;

    return (
        <text x={centreX} y={centreY} textAnchor="middle">
            <tspan
                x={centreX}
                dy="-0.2em"
                fontSize={typography.fontSize['2xl-20px']}
                fontWeight={typography.fontWeights.light}
                fill="#4B535E"
            >
                Total
            </tspan>
            <tspan
                x={centreX}
                dy="1.2em"
                fontSize={typography.fontSize['4xl-32px']}
                fontWeight={typography.fontWeights.bold}
                fill="rgba(44, 53, 66, 0.85)"
            >
                {total}
            </tspan>
        </text>
    );
}

/**
 * Submission Summary donut on the course dashboard, with the total in the centre.
 * All-zero data gets equal slices so the donut still draws.
 * @param {{ category: string, value: number }[]} data Outstanding / Submitted / Not Submitted counts
 */
export default function SubmissionSummaryChart({ data }) {
    const total = data.reduce((runningTotal, slice) => runningTotal + Number(slice.value), 0);
    const slices = data.map((slice) => ({ ...slice, sliceSize: total === 0 ? 1 : Number(slice.value) }));

    return (
        <ChartFrame $height={180} $marginTop={10}>
            <ResponsiveContainer width="100%" height="100%">
                <PieChart margin={CHART_MARGIN}>
                    <Pie
                        data={slices}
                        dataKey="sliceSize"
                        nameKey="category"
                        innerRadius="80%"
                        outerRadius="100%"
                        stroke={tokens.colors.neutral.white}
                        strokeWidth={1}
                        {...PIE_ANGLES}
                    >
                        {slices.map((slice, index) => (
                            <Cell key={slice.category} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                        ))}
                    </Pie>
                    {slices.length > 0 && <DonutTotal total={total} />}
                    <Tooltip formatter={(sliceSize, category, sliceEntry) => sliceEntry.payload.value} />
                    <Legend
                        layout="vertical"
                        align="right"
                        verticalAlign="middle"
                        iconType="circle"
                        iconSize={8}
                        itemSorter={null}
                        formatter={(category) => <LegendText>{category}</LegendText>}
                    />
                </PieChart>
            </ResponsiveContainer>
        </ChartFrame>
    );
}
